import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { patronTypesApi, type PatronType } from "@/lib/api/patron-types";
import { paidAccessApi, type PricingRuleInput } from "@/lib/api/paid-access";
import { useToast } from "@/contexts/ToastContext";
import { Loader2 } from "lucide-react";
import { PaidAccessRuleRow } from "./PaidAccessRuleRow";
import { presetToDays, rulesToRows, type RuleRow } from "./paid-access-rules.types";
import type { PaidAccessPricingRule } from "./paid-access-admin.types";
import { PaidAccessVisibilityAlert } from "./PaidAccessVisibilityAlert";

interface PaidAccessRulesEditorProps {
  digitalResourceId: string;
  initialRules?: PaidAccessPricingRule[] | null;
  isPublic?: boolean;
  onRequirePaymentChange?: (required: boolean) => void;
  onPaidAccessDisabled?: () => void;
  onRestorePublicAccess?: () => void | Promise<void>;
  onSaved?: () => void;
}

export function PaidAccessRulesEditor({
  digitalResourceId,
  initialRules,
  onRequirePaymentChange,
  isPublic = false,
  onPaidAccessDisabled,
  onRestorePublicAccess,
  onSaved,
}: PaidAccessRulesEditorProps) {
  const toast = useToast();
  const [patronTypes, setPatronTypes] = useState<PatronType[]>([]);
  const [requirePayment, setRequirePayment] = useState(false);
  const [rows, setRows] = useState<RuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showVisibilityHint, setShowVisibilityHint] = useState(false);
  const [restorePublicOpac, setRestorePublicOpac] = useState(true);
  const onRequirePaymentChangeRef = useRef(onRequirePaymentChange);
  onRequirePaymentChangeRef.current = onRequirePaymentChange;

  const resolvedInitialRules = initialRules ?? undefined;
  const initialRulesKey =
    resolvedInitialRules === undefined
      ? "fetch"
      : resolvedInitialRules.map((r) => `${r.patronTypeId}:${r.price}`).join("|");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const types = await patronTypesApi.getAll(false);
        if (cancelled) return;
        setPatronTypes(types.filter((t) => t.isActive));

        const rules =
          resolvedInitialRules !== undefined
            ? resolvedInitialRules
            : await paidAccessApi.getPricingRules(digitalResourceId);

        if (cancelled) return;
        const hasRules = rules.length > 0;
        setRequirePayment(hasRules);
        onRequirePaymentChangeRef.current?.(hasRules);
        setRows(rulesToRows(rules));
      } catch {
        toast.error("Could not load paid access settings.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [digitalResourceId, initialRulesKey, resolvedInitialRules, toast]);

  const addRow = () => {
    const unused = patronTypes.find((pt) => !rows.some((r) => r.patronTypeId === pt.id));
    if (!unused) return;
    setRows((prev) => [
      ...prev,
      {
        patronTypeId: unused.id,
        price: 0,
        currency: "NGN",
        accessDurationDays: null,
        durationPreset: "permanent",
      },
    ]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const rules: PricingRuleInput[] = requirePayment
        ? rows.map((r) => ({
            patronTypeId: r.patronTypeId,
            price: r.price,
            currency: r.currency ?? "NGN",
            accessDurationDays: presetToDays(r.durationPreset, r.accessDurationDays ?? undefined),
          }))
        : [];
      await paidAccessApi.savePricingRules(digitalResourceId, rules);
      toast.success("Paid access rules saved.");
      const stillRequired = rules.length > 0;
      onRequirePaymentChange?.(stillRequired);
      if (!stillRequired) {
        setShowVisibilityHint(true);
        onPaidAccessDisabled?.();
        if (restorePublicOpac && !isPublic && onRestorePublicAccess) {
          await onRestorePublicAccess();
        }
      }
      onSaved?.();
    } catch {
      toast.error("Failed to save paid access rules.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Paid access</CardTitle>
        <CardDescription>
          PatronType pricing for the authenticated portal. Paid resources stay off public OPAC.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="requirePayment"
            checked={requirePayment}
            onCheckedChange={(c) => {
              const on = !!c;
              setRequirePayment(on);
              onRequirePaymentChange?.(on);
              if (!on) {
                setShowVisibilityHint(true);
                onPaidAccessDisabled?.();
              } else {
                setShowVisibilityHint(false);
              }
            }}
          />
          <Label htmlFor="requirePayment">Require payment (PatronType rules)</Label>
        </div>
        {showVisibilityHint && !requirePayment && (
          <PaidAccessVisibilityAlert
            showRestoreOption={!isPublic}
            restorePublic={restorePublicOpac}
            onRestorePublicChange={setRestorePublicOpac}
          />
        )}
        {requirePayment &&
          rows.map((row, index) => (
            <PaidAccessRuleRow
              key={row.patronTypeId}
              row={row}
              index={index}
              patronTypes={patronTypes}
              onChange={(i, next) => setRows((prev) => prev.map((r, j) => (j === i ? next : r)))}
            />
          ))}
        <div className="flex flex-wrap items-center gap-2">
          {requirePayment && (
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              Add patron type
            </Button>
          )}
          <Button type="button" onClick={save} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save paid access rules
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
